import { isCancel } from 'axios';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  GetAvailableTargetsPayload,
  GetAvailableTargetsResponse,
  Target,
  TargetPayload,
} from './targets-client.type';

const axiosInstance = AxiosInstance();
const targetsBaseURL = appConfig.VITE_APP_API_URL + '/api/targets/';

/**
 * Fetches targets based on provided filters
 *
 * @param {string} organizationId - The ID of the organization
 * @param {string} portfolioId -  Portfolio ID filter
 * @param {string} [projectId] - Optional project ID filter
 * @param {string} [subprojectId] - Optional subproject ID filter
 * @returns {Promise<Target[]>} - A promise that resolves to an array of targets
 */
const fetchTargets = (
  organizationId: string,
  portfolioId: string,
  projectId?: string,
  subprojectId?: string,
): Promise<Target[]> => {
  let apiURL = targetsBaseURL + '?organization_id=' + organizationId + '&portfolio_id=' + portfolioId;

  if (projectId) {
    apiURL += '&project_id=' + projectId;
  }

  if (subprojectId) {
    apiURL += '&subproject_id=' + subprojectId;
  }

  return axiosInstance
    .get(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching targets'));
};

/**
 * Creates a new target
 *
 * @param {Partial<TargetPayload>} target - The target data to create
 * @returns {Promise<Target>} - A promise that resolves to the created target
 */
const createTarget = (target: Partial<TargetPayload>): Promise<Target> => {
  return axiosInstance
    .post(targetsBaseURL, target)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while creating target'));
};

/**
 * Updates an existing target
 *
 * @param {Partial<TargetPayload>} target - The target data to update (must include id)
 * @returns {Promise<Target>} - A promise that resolves to the updated target
 */
const updateTarget = (target: Partial<TargetPayload>): Promise<Target> => {
  return axiosInstance
    .put(`${targetsBaseURL}${target.id}/`, target)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while updating target'));
};

/**
 * Deletes a target by ID
 *
 * @param {string} targetId - The ID of the target to delete
 * @returns {Promise<void>} - A promise that resolves when the target is deleted
 */
const deleteTarget = (targetId: string): Promise<void> => {
  return axiosInstance
    .delete(`${targetsBaseURL}${targetId}/`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while deleting target'));
};

/**
 * Fetches available targets based on provided payload
 *
 * @param {GetAvailableTargetsPayload} payload - The payload containing filter criteria
 * @returns {Promise<GetAvailableTargetsResponse>} - A promise that resolves to the available targets response
 */
const fetchAvailableTargets = (
  payload: GetAvailableTargetsPayload,
  signal?: AbortSignal,
): Promise<GetAvailableTargetsResponse> => {
  return axiosInstance
    .get(targetsBaseURL + 'available-targets/', { params: payload, signal })
    .then((response) => response.data)
    .catch((error) => {
      // Re-throw axios cancellation errors unchanged so TanStack Query can
      // recognize them as cancellations (and avoid setting query.error /
      // triggering retries) when the caller's AbortSignal fires. Wrapping
      // them in a generic string would mask the cancellation marker.
      if (isCancel(error)) {
        return Promise.reject(error);
      }
      return Promise.reject(error?.response?.data || 'Error while fetching available targets');
    });
};

export { createTarget, deleteTarget, fetchAvailableTargets, fetchTargets, updateTarget };
